import { useEffect, useMemo, useState } from 'react';
import styles from './OverviewForm.module.scss';
import { SllLesson } from '@src/api/graphql-codegen';
import { SelectOption } from '@src/types';
import NewCategories from '@src/components/categories/NewCategories';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { IconBrain, IconClock, IconCourses, IconGrade, IconInfo, countryOptions } from '@src/constants';
import { hasArrayValues } from '@src/utils/helpers';

type Props = {
  lesson?: SllLesson
  skills?: SelectOption[]
  isAusPreProgramQuiz?: boolean;
};

function numberToWord(number: any) {
  const ones = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

  if (number < 0 || number > 99) {
      return 'Number out of range';
  }

  if (number < 10) {
    return ones[number];
  } else if (number < 20) {
    return teens[number - 10];
  }
}

function getGradeValue(grade: string) {
  if (grade === 'Pre-K') return -1;
  if (grade === 'Kindergarten') return 0;
  const gradeMatch = grade.match(/Grade (\d+)/);
  if (gradeMatch) return parseInt(gradeMatch[1], 10);
  return Infinity; // For any unrecognized grades
}

function OverviewForm(props: Props) {
  const { lesson, isAusPreProgramQuiz=false } = props;

  const [box1, setBox1] = useState<string[]>([]);
  const [helpfulHeading, setHelpfulHeading] = useState('This Lesson is Helpful For Students Who:');
  // const [requirement, setRequirement] = useState('This lesson only contains downloadable materials.')
  const [objectAnswers, setObjectAnswers] = useState<string[]>([]);
  const [competency, setCompetency] = useState<string[]>([]);
  const [isCaregiver, setIsCaregiver] = useState(false);
  const [isNewCover, setIsNewCover] = useState(false)

  const isAussie = lesson?.country === countryOptions[1].label

  useEffect(() => {
    if (lesson) {
      setBox1(JSON.parse(lesson?.overviewAnswers1 || '[]'));
      setHelpfulHeading(lesson.overviewHeading1 || 'This Lesson is Helpful For Students Who:');
      setObjectAnswers(JSON.parse(lesson.overviewAnswers2 || '[]'));
      setCompetency(JSON.parse(lesson.competency || '[]'));
      setIsCaregiver(lesson.lessonType === '7')
      setIsNewCover(!!lesson.categories)
    }
  }, [lesson])

  const ageGroupVal = useMemo(() => {
    if (!lesson) return '';
    if (isAussie) {
      if (lesson?.ageGroup?.includes('[')) {
        let ary = JSON.parse(lesson?.ageGroup);
        ary = ary.map(item => {
          if (item.includes('Kindy')) return item;
          let tmp = item.replace('Year ', '');
          if (item.includes('-')) {
            return numberToWord(Number(tmp.split('-')[0]))
              + '-' + numberToWord(Number(tmp.split('-')[1]));
          }
          return numberToWord(Number(tmp));
        });
        return ary.join(', ');
      } else {
        if (lesson?.ageGroup?.includes('Kindy')) return lesson?.ageGroup
      }
      return numberToWord(Number(lesson?.ageGroup?.replace('Year ', '')))
    } else {
      try {
        const grades = JSON.parse((lesson?.gradeLevel as string) ?? '[]') as string[]
        return grades.sort((a, b) => getGradeValue(a) - getGradeValue(b)).map(g => {
          if (g.includes('Grade ')) {
            return numberToWord(Number(g.replace('Grade ', '')))
          }
          return g
        }).join(', ')
      } catch (err) {
        return ''
      }
    }
  }, [isAussie, lesson])

  return (
    <div className={styles.container}>
      {isNewCover
        ? (
            <div className={styles.newWrapper}>
              <div className={styles.infoView}>
                <div className={isAussie ? styles.ausInfoWrapper : styles.infoWrapper}>
                  <div className={styles.contentView} style={{paddingTop: 20}}>
                    <div className={styles.infoContent}>
                      <div style={{minWidth: 20, minHeight: 30, display: 'flex', alignItems: 'center'}}>
                        <LordIcon
                          src={IconGrade}
                          isHover={true}
                          colors={{
                            primary: '#274649',
                            secondary: '#274649'
                          }}
                          size={20}
                          target='.OverviewForm_infoContent__w8l2K'
                        />
                      </div>
                      <div className={styles.tagLabel}>{isAussie ? 'Year:' : 'Grade:' }</div><span>{ageGroupVal}
                      </span>
                    </div>
                    {hasArrayValues(competency) && <div className={styles.infoContent}>
                      <div style={{minWidth: 20, minHeight: 30, display: 'flex', alignItems: 'center'}}>
                        <LordIcon
                          src={IconCourses}
                          isHover={true}
                          colors={{
                            primary: '#274649',
                            secondary: '#274649'
                          }}
                          size={20}
                          target='.OverviewForm_infoContent__w8l2K'
                        />
                      </div>
                      <div className={styles.tagLabel}>Competency: </div>
                      <span>{competency.toString().replace(',', ', ')}</span>
                    </div>}
                    {((lesson?.country === countryOptions[1].label && lesson?.suggestedDuration) || (lesson?.country !== countryOptions[1].label && lesson?.duration)) ? <div className={styles.infoContent}>
                      <div style={{minWidth: 20, minHeight: 30, display: 'flex', alignItems: 'center'}}>
                        <LordIcon
                          src={IconClock}
                          isHover={true}
                          colors={{
                            primary: '#274649',
                            secondary: '#274649'
                          }}
                          size={20}
                          target='.OverviewForm_infoContent__w8l2K'
                        />
                      </div>
                      <div className={styles.tagLabel}>Suggested Duration: </div>
                      <span>{lesson?.country === countryOptions[1].label ? lesson.suggestedDuration : lesson?.duration} min</span>
                    </div> : null}
                  </div>
                </div>
                <div className={isAussie ? styles.ausCategoryWrapper : styles.categoryWrapper}>
                  {lesson?.lessonType === '4' ? <div className={styles.contentView}>
                    <div className={styles.onboardingDetailView}>
                      <LordIcon src={IconInfo}
                        isHover={true} 
                        colors={{
                          primary: '#AF77B8',
                          secondary: '#FFB073'
                        }}
                        size={20}
                      />
                      <span>{lesson.lessonDetails}</span>
                    </div>
                  </div> : <div className={styles.contentView}>
                    <div className={styles.categoryView}>
                      <NewCategories categories={lesson?.categories ?? ''} lessonType={lesson?.lessonType ?? '0'} isAussie={isAussie} isExtensionAvailable={lesson?.isExtensionAvailable ?? false} />
                    </div>
                  </div>}
                </div>
              </div>
              
            </div>
          )
        : (
            <div className={styles.wrapper}>
              <div className={styles.subWrapper}>
                {isCaregiver
                ? <div className={styles.contentView}>
                    <div className={styles.header}>
                      <div className={styles.subTitle}>
                        {'Learning Objectives:'}
                      </div>
                    </div>
                    <ul className={styles.boxView}>
                    {(objectAnswers ?? []).map((o, index) => {
                      const optionStr: string = o as string;
                      return (
                        <li key={index} className={styles.questionBox}>
                          {optionStr}
                        </li>
                      );
                    })}
                    </ul>
                  </div>
                : <div className={styles.contentView}>
                  <div className={styles.infoContent}>
                    <LordIcon
                        src={IconGrade}
                        isHover={true}
                        colors={{
                          primary: '#274649',
                          secondary: '#274649'
                        }}
                        size={20}
                        target='.OverviewForm_infoContent__w8l2K'
                      /><div className={styles.subContent}>{isAussie ? 'Year:' : 'Grade:'} &nbsp;<span>{ageGroupVal}</span></div>
                  </div>
                  {!isAusPreProgramQuiz && hasArrayValues(competency) &&
                    <div className={styles.infoContent}>
                      <LordIcon
                        src={IconBrain}
                        isHover={true}
                        colors={{
                          primary: '#274649',
                          secondary: '#274649'
                        }}
                        size={20}
                        target='.OverviewForm_infoContent__w8l2K'
                      />
                    <div className={styles.subContent}>Competency: &nbsp;<span>{competency.toString().replace(',', ', ')}</span></div>
                  </div>
                  }
                  <div className={styles.infoContent}>
                      <LordIcon
                        src={IconClock}
                        isHover={true}
                        colors={{
                          primary: '#274649',
                          secondary: '#274649'
                        }}
                        size={20}
                        target='.OverviewForm_infoContent__w8l2K'
                      />
                    <div className={styles.subContent}>Suggested Time: &nbsp;<span>{lesson?.duration} minutes</span></div>
                  </div>
                </div>}
                {
                // lesson?.isOnboarding ? <div className={styles.contentView}>
                //     <div className={styles.onboardingDetailView}>
                //       <LordIcon src={IconInfo}
                //         isHover={true} 
                //         colors={{
                //           primary: '#AF77B8',
                //           secondary: '#FFB073'
                //         }}
                //         size={20}
                //       />
                //       <span>{lesson.lessonDetails}</span>
                //     </div>
                //   </div> : 
                  <div className={styles.contentView}>
                  <div className={styles.header}>
                    <div className={styles.subTitle}>
                      {isCaregiver ? 'At a Glance:' : helpfulHeading}
                    </div>
                  </div>
                  {isCaregiver || isAusPreProgramQuiz ?
                    <ul className={styles.boxView}>
                    {(box1 ?? []).map((o, index) => {
                      const optionStr: string = o as string;
                      return (
                        <li key={index} className={styles.questionBox}>
                          {optionStr}
                        </li>
                      );
                    })}
                    </ul>
                  : <div className={styles.tagView}>
                  {(box1 ?? []).map((o, index) => {
                    const optionStr: string = o as string;
                    return (
                      <div key={index} className={styles.tagBox}>
                        {optionStr}
                      </div>
                    );
                  })}
                  </div>}
                </div>}
              </div>
            </div>
          )
      }
    </div>
  );
}

export default OverviewForm;